import { defineStore } from 'pinia';
import type { Component } from 'vue';
import ConfirmationDialog from './common-dialogs/ConfirmationDialog.vue';
import PromptDialog from './common-dialogs/PromptDialog.vue';

export type TypedDialog<TResult, TProps extends Record<string, any> = any> = Component<TProps> & {
    readonly __dialogResultType?: TResult;
};

export type PropsOf<C> = C extends new (...args: any) => any ? InstanceType<C>['$props'] : never;

type Dialog<C extends Component> = {
    component: C;
    props?: PropsOf<C>;
    resolve: (value: any) => void;
};

/**
 * Central dialogs store
 *
 * To infer return type:
 * ```ts
 * import { type TypedDialog } from '@/stores/dialogs.store';
 * import _ConfirmationDialog from './ConfirmationDialog.vue';
 *
 * export type ConfirmationDialogResult = { confirmed: boolean };
 * const ConfirmationDialog = _ConfirmationDialog as TypedDialog<ConfirmationDialogResult, PropsOf<typeof _ConfirmationDialog>>;
 * export default ConfirmationDialog;
 * ```
 */
export const useDialogs = defineStore(
    'ui-dialogs',
    () => {
        const visibleDialogs = shallowRef<Dialog<any>[]>([]);

        async function open<TResult, TProps extends Record<string, any>>(component: TypedDialog<TResult, TProps>, props?: TProps): Promise<TResult> {
            return new Promise((resolve) => {
                visibleDialogs.value = [...visibleDialogs.value, { component, props, resolve }];
            });
        }

        async function confirm(props: PropsOf<typeof ConfirmationDialog>): Promise<boolean | undefined> {
            return await open(ConfirmationDialog, props);
        }

        async function prompt(props: PropsOf<typeof PromptDialog>): Promise<string | undefined> {
            return await open(PromptDialog, props);
        }

        function close(result?: any) {
            const dialogs = visibleDialogs.value;
            const dialog = dialogs.pop();
            visibleDialogs.value = [...dialogs];
            dialog?.resolve(result);
        }

        return { visibleDialogs, open, close, confirm, prompt };
    },
    { persistedState: { persist: false } },
);
